import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ConfigProvider, Modal, Spin } from "antd";
import { GoogleOAuthProvider } from "@react-oauth/google";

import "antd/es/locale/de_DE";
import "antd/es/locale/it_IT";
import "antd/es/locale/pt_PT";
import "antd/es/locale/fr_FR";
import "antd/es/locale/cs_CZ";
import "antd/es/locale/pl_PL";
import "antd/es/locale/es_ES";

import deDE from "antd/es/locale/de_DE";
import itIT from "antd/es/locale/it_IT";
import ptPt from "antd/es/locale/pt_PT";
import frFr from "antd/es/locale/fr_FR";
import csCZ from "antd/es/locale/cs_CZ";
import plPL from "antd/es/locale/pl_PL";
import esEs from "antd/es/locale/es_ES";
import defaultLocale from "antd/es/locale/en_US";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import AppRouter from "./AppRouter";
import { FormDirtyProvider } from "../../hooks/useFormDirty";

import Sidebar from "./Sidebar";
import useApp from "../../hooks/useApp";
import { AntOverride } from "./antOverride";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import ErrorBoundary from "../../ErrorBoundary";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import Login from "../../Auth/Login";
import PasswordResetFinish from "../../Auth/PasswordResetFinish";
import PasswordResetInit from "../../Auth/PasswordResetInit";
import { GetXsrfToken } from "../../helpers/eGateApi";
import { authorizeLoginCookieVerify, unwrap } from "../../api";
import { useEffectOnce, useToggle } from "react-use";
import { AuthorizeWebResponse, ServerInfoDto } from "../../api/types";
import * as Sentry from "@sentry/browser";
import useHotkeyShortcuts from "../../hooks/useHotkeyShortcuts";
import FullPageSpinnerStyler from "../../FullPageSpinnerStyler";
import classNames from "classnames";
import useServerInfo from "../../hooks/useServerInfo";
import { getDefaultView, useGetView } from "../../helpers/utils";
import upperFirst from "lodash-es/upperFirst";
import { appVersion } from "../../version";
const sentryUrl = "https://51ccb51a08dd9e6961d63ecbc1117494@o4511789482770432.ingest.de.sentry.io/4511806961549392";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(timezone);
import AutomaticLogout from "./AutomaticLogout";
import ChatBot from "../../ChatBot/ChatBot";
import { makeFetchTransport } from "@sentry/browser";
import DeepLink from "../../deeplinks/DeepLink";
import { clearAnalyticsContext, setAnalyticsContext } from "../../helpers/analytics";
import { ROLE } from "../../Auth/useRole";

document.addEventListener("keydown", function (zEvent) {
  if (zEvent.ctrlKey && zEvent.key === "i") {
    try {
      console.log("SENTRY CHECK");
      throw new Error("SENTRY CHECK");
    } catch (e) {
      Sentry.captureException(e);
    }
  }
});

// App content style classes
const appContentClasses = `
  w-[var(--content-width)]
  [&.collapsed]:w-[var(--content-width-collapsed)]
`;

const theme = {
  token: {
    controlOutline: "transparent",
    colorErrorOutline: "transparent",
    colorWarningOutline: "transparent",
    controlOutlineWidth: 0,
    colorPrimary: "#27CA7C",
    borderRadius: 10,
    colorLink: "#03AF5C",
    colorText: "#000",
    colorTextSecondary: "#000",
    colorIcon: "#999",
    fontSize: 15,
    controlHeight: 30,
    colorLinkHover: "#03AF5C",
    colorLinkActive: "#03AF5C",
    colorSuccess: "#27CA7C",
    colorWarning: "#E4A915",
    motion: false,
  },
  components: {
    Tabs: { paddingContentHorizontalLG: 8 },
    Table: {
      fontSizeSM: 12,
      paddingContentVerticalSM: 6,
      paddingContentHorizontalSM: 5,
      fontWeightStrong: 500,
      fontSize: 14,
    },
    Empty: { margin: 0 },
    Button: {
      paddingContentHorizontal: 12,
      fontWeightStrong: 500,
      colorError: "#f16d43",
      controlHeight: 30,
      colorPrimaryBg: "#03AF5C",
      defaultHoverColor: "#03AF5C",
      defaultHoverBorderColor: "#03AF5C",
    },
    Menu: { paddingContentHorizontal: 16, colorBgBase: "rgba(0, 175, 91, 0.5)", itemMarginInline: 0 },
    List: { paddingContentHorizontalSM: 0, paddingContentHorizontal: 0, paddingContentVerticalSM: 2 },
    Image: { zIndexBase: 2001 },
    Modal: { zIndexBase: 1020, colorPrimary: "#03AF5C" },
    Notification: { colorPrimary: "#03AF5C" },
    Select: { fontSize: 13, controlHeight: 30 },
    Upload: {
      padding: 0,
    },
    Alert: { defaultPadding: "2px 10px" },
    Form: { verticalLabelPadding: "0 0 5px", itemMarginBottom: 8 },
  },
};

function initSentry(serverInfo: ServerInfoDto) {
  const environment = upperFirst(serverInfo?.deployment);
  const isDev = import.meta.env.DEV;
  Sentry.init({
    enabled: !isDev && serverInfo.version != "1.0.0.0",
    dsn: sentryUrl,
    release: `egate-cockpit@${appVersion}`,
    environment,
    tunnel: "/api/v1/client-errors-tunnel",
    attachStacktrace: false,
    // Structured logs for usage-analytics error events (see helpers/analytics.ts)
    enableLogs: true,

    transport: (baseOptions) => {
      const options = { ...baseOptions };

      const baseTransport = makeFetchTransport(options);

      return {
        send: (envelope: any) => {
          // ✅ refresh token for every request
          options.headers = {
            ...(options.headers ?? {}),
            "X-XSRF-TOKEN": GetXsrfToken() ?? "",
          };

          return baseTransport.send(envelope);
        },
        flush: (timeout: any) => baseTransport.flush(timeout),
      };
    },

    beforeSend(event, hint) {
      const error = hint.originalException as any;
      if (error?.name === "AbortError") return null;
      return event;
    },
  });

  return serverInfo;
}
export function App() {
  const { i18n } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isFetching: serverInfoFetching } = useServerInfo();
  const { appData, login, resetApp, isFetching } = useApp();

  const isUserLoggedIn = appData?.auth?.user;
  const isCollapsed = appData?.collapsed;
  const { xs, sm, md } = useBreakpoint();
  const matches = xs || sm || md;
  const collapsedMargin = "60px";
  const mobileMargin = matches ? "240px" : "80px";
  const marginLeft = isCollapsed ? collapsedMargin : mobileMargin;

  useHotkeyShortcuts();

  const [authPending, setAuthPending] = useToggle(true);

  async function callVerify() {
    try {
      const response = (await unwrap(authorizeLoginCookieVerify())) as AuthorizeWebResponse;
      const re = new RegExp("^/reset-password");
      const isResetHistory = location?.pathname !== null ? re.exec(location?.pathname) !== null : false;
      if (response.user) {
        if (!isResetHistory && location.pathname == "/signin") {
          const view = getDefaultView(response);
          navigate(view ?? "/app/dashboard", { replace: true });
        }
        login(response);
      } else {
        if (!isResetHistory && location.pathname !== "/signin") {
          resetApp();

          navigate("/signin", { replace: true, state: location.pathname });
        }
      }
    } catch (e) {
      console.error(e);
    }

    setAuthPending(false);
  }

  useEffectOnce(() => {
    if (!isUserLoggedIn) {
      void callVerify();
    }
  });

  // The cache has to outlive the authenticated views: emptying it while they are mounted lets their
  // queries rebuild and refetch on the session that just ended. Losing auth unmounts them in the
  // render this effect follows, so nothing is left to rebuild by the time it runs.
  const wasLoggedIn = useRef(false);
  useEffect(() => {
    if (wasLoggedIn.current && !isUserLoggedIn) {
      queryClient.clear();
    }
    wasLoggedIn.current = !!isUserLoggedIn;
  }, [isUserLoggedIn]);

  useLayoutEffect(() => {
    Modal.destroyAll();
  }, [location.pathname]);

  function getLocale() {
    switch (i18n.language) {
      case "en":
        return defaultLocale;
      case "de":
        return deDE;
      case "it":
        return itIT;
      case "pt":
        return ptPt;
      case "fr":
        return frFr;
      case "cs":
        return csCZ;
      case "pl":
        return plPL;
      case "es":
        return esEs;
    }
  }

  const mobile = useGetView().isMobile;
  const serverInfo = useServerInfo();

  const [sentryInitiated, setSentryInitiated] = useState(false);
  useEffect(() => {
    if (!serverInfo.data || sentryInitiated) return;
    if (serverInfo.data && !sentryInitiated) {
      initSentry(serverInfo.data);
      setSentryInitiated(true);
    }
  }, [serverInfo.data?.deployment, serverInfo.data?.version]);

  // Usage analytics: enabled only on production and only for customer users
  // (not EMZ staff). Re-evaluated whenever session or server info changes;
  // logout clears auth which disables tracking again.
  const auth = appData?.auth;
  const deployment = serverInfo.data?.deployment;
  const analyticsCustomerId = appData?.selectedCustomer?.id ?? auth?.features?.[0]?.customer?.id;
  useEffect(() => {
    if (!auth?.user || !deployment) {
      clearAnalyticsContext();
      return;
    }
    const isCustomer = !auth.isRoot && !auth.roles?.includes(ROLE.EMZ_DEVELOPER);
    setAnalyticsContext({
      enabled: deployment === "production" && isCustomer,
      customerId: analyticsCustomerId,
    });
  }, [auth?.user, auth?.isRoot, deployment, analyticsCustomerId, sentryInitiated]);

  if (!isUserLoggedIn && authPending && serverInfoFetching && !serverInfo) {
    return (
      <FullPageSpinnerStyler className={"full-page-loader"}>
        <Spin spinning />
      </FullPageSpinnerStyler>
    );
  }
  if (isFetching || authPending) {
    return (
      <FullPageSpinnerStyler className={"full-page-loader"}>
        <Spin spinning />
      </FullPageSpinnerStyler>
    );
  }

  const isApp = location.pathname.includes("/app");

  // Logout clears the query cache while these views are still mounted; without this redirect they
  // re-render, rebuild their queries and fire requests against the session that was just ended.
  if (isApp && !isUserLoggedIn) {
    return <Navigate replace to={"/signin"} />;
  }

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider clientId={serverInfo?.data?.googleClientId as string}>
        <ConfigProvider locale={getLocale()} theme={theme}>
          {isApp && (
            <>
              <AntOverride />
              {!mobile && <Sidebar />}{" "}
            </>
          )}
          <AutomaticLogout />
          <ChatBot />

          <Suspense
            fallback={
              <FullPageSpinnerStyler className={"full-page-loader"}>
                <Spin spinning={false} />
              </FullPageSpinnerStyler>
            }
          >
            <Routes>
              <Route
                element={
                  <>
                    <div
                      className={classNames("isoContentMainLayout", appContentClasses, {
                        "collapsed": isCollapsed,
                        "open": !isCollapsed,
                      })}
                      style={{
                        marginLeft: mobile ? "0" : marginLeft,
                      }}
                    >
                      <FormDirtyProvider>
                        <AppRouter />
                      </FormDirtyProvider>
                    </div>
                  </>
                }
                path={"/app/*"}
              />
              <Route element={<PasswordResetFinish />} path={"/reset-password/:token"} />
              <Route element={<PasswordResetInit />} path={"/reset-password/"} />
              <Route element={<Login />} path={"/signin"} />
              <Route element={<Login />} path={"/"} />
              <Route element={<DeepLink />} path={"/:lang/mobile-app-install"} />
            </Routes>
          </Suspense>
        </ConfigProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export default App;
